import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core'
import { httpResource, HttpResourceRef } from '@angular/common/http'
import { environment } from '../../../environments/environment'
import { ApiResponse } from '../interfaces/api.interface'
import { SeasonService } from './season.service'
import { landingSeason, SEASONS, isShownSeason } from '../constants/season.constants'

export interface Competition {
  id: string
  name: string
  display_name: string
  short_name: string
  scope: string
  age_group: string
  sort_order: number
  gender?: string
  country?: string
  country_code?: string
  country_flag_url?: string
  // Teams that played in this competition in the season the request asked for. Absent on a
  // response from a backend that predates it, which is why the card guards on it.
  team_count?: number
  // Filmed but not analysed (CC, 01/10/2026): the page hides Stats, Rankings and top performers.
  video_only?: boolean
}

export interface Phase {
  id: string
  name: string
  display_name: string
  sort_order: number
  type: string
  /** The edition it belongs to (27/10/2026); null/absent = none. */
  edition_id?: string | null
  participants?: Participant[]
}

/** One edition of a tournament (27/10/2026): the UEFA U15 Development Tournament was played twice
 *  in 2025/26 -- each its own event, never merged. */
export interface Edition {
  id: string
  label: string
  season: string
  hosts: string[]
  starts_on: string
  ends_on: string
  teams: number
  matches: number
  auto_ends_on: string
}

export interface Participant {
  id: string
  name: string
  image_url: string
}

export interface CompetitionDetail extends Competition {
  phases: Phase[]
  // Any season has an analysed match: the "Video only" switch is never offered (CC, 03/10/2026).
  // Absent from a backend that predates it.
  has_analysed_matches?: boolean
  // Seasons it has fixtures in, newest first (role-by-role test, 26/10/2026). Absent from a
  // backend that predates it.
  seasons?: string[]
  // Its editions, newest first -- only the ones given, for a person given some (27/10/2026).
  editions?: Edition[]
  // Where it belongs: 'confederation'/'UEFA', 'world'/'FIFA', 'country'/'CY' (breadcrumb).
  area_kind?: string
  area_code?: string
  // This person was given only some editions: what counts every edition is hidden.
  edition_limited?: boolean
}

@Injectable({ providedIn: 'root' })
export class CompetitionService {
  // Current competition page state
  readonly competitionId = signal<string>('')
  // The season is shared app-wide, not per-section: picking a season on a
  // competition and then opening a team must not reset it. See SeasonService.
  private readonly seasonService = inject(SeasonService)
  readonly season = this.seasonService.season

  /** Seeds the default season only when none is set. */
  seedSeason (): void { this.seasonService.seedIfUnset() }

  // Bumped by the resolver each time a competition page is opened (not on a tab change), and
  // the opening it was settled for. Once per opening, so a season the user then picks is never
  // overridden -- the same rule as team pages.
  private readonly landingRequest = signal(0)
  private landedFor = ''

  /** Called on opening a competition page: settle its season once its seasons are known. */
  requestLanding (): void { this.landingRequest.update(n => n + 1) }

  constructor () {
    // A competition with no fixtures in the season being browsed opens on its newest season
    // instead of an empty page (role-by-role test 26/10/2026: the UEFA U16 Development
    // Tournament, played in May 2026, opened empty on 2026/2027 for its tournament director).
    effect(() => {
      const request = this.landingRequest()
      if (request === 0) return
      const c = this.competition()
      // The previous competition's detail lingers while the next one loads: wait for this one.
      if (!c || c.id !== this.competitionId() || !c.seasons) return
      const key = `${request}:${c.id}`
      if (key === this.landedFor) return
      this.landedFor = key
      const season = untracked(() => landingSeason(this.season(), c.seasons))
      if (season) untracked(() => this.seasonService.set(season))
    })
    // Browsed by edition: the season follows the edition, so the season-scoped requests ask for
    // the edition's own season.
    effect(() => {
      const e = this.edition()
      if (e?.season && e.season !== untracked(() => this.season())) untracked(() => this.seasonService.set(e.season))
    })
  }

  // Detail resource — fetches GET /competitions/:id
  readonly competitionResource: HttpResourceRef<CompetitionDetail | undefined> = httpResource<CompetitionDetail | undefined>(
    () => {
      const id = this.competitionId()
      if (!id) return undefined
      // The season is what scopes team_count on this response. Sent only once known, so a
      // caller that has not set one asks exactly the URL it always did.
      const season = this.season()
      const query = season ? `?season=${encodeURIComponent(season)}` : ''
      return `${environment.apiUrl}/competitions/${id}${query}`
    },
    {
      defaultValue: undefined,
      parse: (raw: unknown) => (raw as ApiResponse<CompetitionDetail>).data,
    },
  )

  // .value() throws on an errored resource. This is read from inside the URL computation
  // of sibling resources (matches reads phases() to pick a phase), so an unguarded read
  // here throws before those components can render their own error state.
  readonly competition = computed(() =>
    this.competitionResource.hasValue() ? this.competitionResource.value() : undefined,
  )
  readonly competitionUnavailable = computed(() => !!this.competitionResource.error())
  // Two national competitions carry an empty display_name, and `??` does not catch ''.
  readonly competitionDisplayName = computed(() => {
    const c = this.competition()
    return c?.display_name || c?.name || 'Competition'
  })
  readonly competitionName = computed(() => this.competition()?.name ?? '')
  // Browsed by edition: that edition's phases only (27/10/2026).
  readonly phases = computed(() => {
    const all = this.competition()?.phases ?? []
    const e = this.edition()
    return e ? all.filter(p => p.edition_id === e.id) : all
  })
  /** Filmed but not analysed: no Stats, Rankings or top performers to show. */
  readonly videoOnly = computed(() => !!this.competition()?.video_only)

  // ── Editions (27/10/2026, role-by-role C.5) ──────────────────────────────────
  // A tournament played more than once in a season (or given by edition) is browsed one edition
  // at a time: an Edition picker replaces the season picker, and every panel keeps to it.

  /** The edition picked; '' = the newest. */
  readonly editionPick = signal<string>('')
  readonly editions = computed<Edition[]>(() => this.competition()?.editions ?? [])

  /** Browsed by edition: given only some editions, or some season holds two or more. */
  readonly byEdition = computed(() => {
    const c = this.competition()
    const eds = this.editions()
    if (!c || !eds.length) return false
    if (c.edition_limited) return true
    const perSeason = new Map<string, number>()
    for (const e of eds) perSeason.set(e.season, (perSeason.get(e.season) ?? 0) + 1)
    return [...perSeason.values()].some(n => n > 1)
  })

  /** The edition every panel keeps to; null when the competition is not browsed by edition. */
  readonly edition = computed<Edition | null>(() => {
    if (!this.byEdition()) return null
    const eds = this.editions()
    return eds.find(e => e.id === this.editionPick()) ?? eds[0] ?? null
  })

  /** Appended to every competition request: "&edition_id=…", or '' for the whole competition. */
  readonly editionQuery = computed(() => {
    const e = this.edition()
    return e ? `&edition_id=${encodeURIComponent(e.id)}` : ''
  })

  pickEdition (id: string): void { this.editionPick.set(id) }

  /** The seasons the picker offers: the ones this competition has fixtures in, newest first.
   *  The known list until the backend says, so a page never offers nothing. */
  readonly seasons = computed<readonly string[]>(() => {
    const has = (this.competition()?.seasons ?? []).filter(isShownSeason)
    return has.length ? [...has].sort().reverse() : SEASONS
  })
}
